import { EARTH_RADIUS_M } from '@app/consts';

/**
 * Calculate distance between two coordinates using the Haversine formula.
 * @param lat1 - Latitude of the first point in degrees
 * @param lon1 - Longitude of the first point in degrees
 * @param lat2 - Latitude of the second point in degrees
 * @param lon2 - Longitude of the second point in degrees
 * @returns Distance in meters
 */
export function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

    const lat1Rad = toRadians(lat1);
    const lat2Rad = toRadians(lat2);
    const deltaLat = toRadians(lat2 - lat1);
    const deltaLon = toRadians(lon2 - lon1);

    const a =
        Math.sin(deltaLat / 2) ** 2 +
        Math.cos(lat1Rad) * Math.cos(lat2Rad) * Math.sin(deltaLon / 2) ** 2;

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return EARTH_RADIUS_M * c; // Distance in meters
}

/**
 * Format distance according to rules:
 * - Below 75m: show as "50 m"
 * - Below 950m: rounded to nearest 100m (200m, 300m, etc.)
 * - Below 9.95km: rounded to km with 1 decimal place (1000m -> "1 km", 1400m -> "1.4 km", 7600m -> "7.6 km", )
 * - Above 9.95km: rounded to nearest km (9950m -> "10 km", 16000m -> "16 km")
 * @param distanceInMeters - Distance in meters
 * @returns Formatted distance string
 */
export function formatDistance(distanceInMeters: number): string {
    const format = (num: number | string) => num.toString().replace('.', ',');

    if (distanceInMeters > 949) {
        const distanceInKm = distanceInMeters / 1000;

        if (distanceInMeters > 9949) {
            // to nearest km with 1 decimal place
            const km = Math.round(distanceInKm);
            return `${format(km)} km`;
        }

        // Round to nearest kilometer
        const km = distanceInKm % 1 === 0 ? distanceInKm : distanceInKm.toFixed(1);
        return `${format(km)} km`;
    }

    if (distanceInMeters < 75) {
        return '50 m';
    }

    // Round to nearest 100 meters
    const roundedMeters = Math.round(distanceInMeters / 100) * 100;
    return `${roundedMeters} m`;
}
